import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useGoBack } from "../../hooks/useGoBack";
import { motion, AnimatePresence } from "framer-motion";

import { apiPost } from "../../apiService";
import { getErrorMessage } from "../../utils/errorHelper";

/* Reuse the website auth theme already defined for Login / ForgotPassword */
import "./Login.scss";

/* --- Centralized SVG Icons (matches Login.jsx / ForgotPassword.jsx) --- */
const Icons = {
  Shield: () => (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeWidth="2"
      width="1.2em"
      height="1.2em"
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
    </svg>
  ),
  ChevronLeft: () => (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeWidth="2.5"
      width="1.2em"
      height="1.2em"
    >
      <polyline points="15 18 9 12 15 6"></polyline>
    </svg>
  ),
  Eye: () => (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeWidth="2"
      width="1.8rem"
      height="1.8rem"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
      <circle cx="12" cy="12" r="3"></circle>
    </svg>
  ),
  EyeOff: () => (
    <svg
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      strokeWidth="2"
      width="1.8rem"
      height="1.8rem"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
      <line x1="1" y1="1" x2="23" y2="23"></line>
    </svg>
  ),
};

/* --- Helper UI Components --- */

const AuthCard = ({ children }) => (
  <motion.div
    className="auth-card"
    initial={{ opacity: 0, y: 30, scale: 0.98 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
  >
    {children}
  </motion.div>
);

/* Branding visual for the left panel — themed around setting a new password */
const NewPasswordVisual = () => (
  <div className="core-engine">
    <svg viewBox="0 0 400 400" fill="none" className="engine-svg">
      <defs>
        <filter id="newpass-glow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="10" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <linearGradient id="newpass-ring" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.25" />
          <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0.06" />
        </linearGradient>
        <linearGradient
          id="newpass-lock-body"
          x1="0%"
          y1="0%"
          x2="0%"
          y2="100%"
        >
          <stop offset="0%" stopColor="#eef2ff" />
          <stop offset="100%" stopColor="#ffffff" />
        </linearGradient>
      </defs>

      {/* Orbiting ring */}
      <circle
        cx="200"
        cy="200"
        r="150"
        stroke="url(#newpass-ring)"
        strokeWidth="1.5"
        strokeDasharray="8 14"
        className="anim-spin"
      />

      {/* Soft connection arc */}
      <path
        d="M60 220 Q 200 80, 340 220"
        stroke="#e0e7ff"
        strokeWidth="2"
        fill="none"
        className="anim-float"
      />

      {/* Central padlock (closed = secured) */}
      <g filter="url(#newpass-glow)">
        <path
          d="M170 175 V 155 a30 30 0 0 1 60 0 V 175"
          stroke="#4f46e5"
          strokeWidth="6"
          strokeLinecap="round"
          fill="none"
          className="anim-pulse"
        />
        <rect
          x="150"
          y="175"
          width="100"
          height="80"
          rx="16"
          fill="url(#newpass-lock-body)"
          stroke="#4f46e5"
          strokeWidth="2.5"
        />
        <circle cx="200" cy="210" r="7" fill="#4f46e5" />
        <rect x="197" y="214" width="6" height="16" rx="2" fill="#4f46e5" />
      </g>

      {/* Floating key */}
      <g
        className="anim-float"
        style={{ animationDelay: "0.2s" }}
        transform="translate(72 92)"
      >
        <circle
          cx="14"
          cy="14"
          r="10"
          fill="#ffffff"
          stroke="#4f46e5"
          strokeWidth="2"
        />
        <path
          d="M24 14 L48 14 M40 14 L40 22 M44 14 L44 20"
          stroke="#4f46e5"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </g>

      <circle
        cx="310"
        cy="110"
        r="8"
        fill="#38bdf8"
        className="anim-float"
        style={{ animationDelay: "0.8s" }}
      />
      <circle
        cx="200"
        cy="330"
        r="10"
        fill="#a5b4fc"
        className="anim-float"
        style={{ animationDelay: "0.5s" }}
      />
      <circle
        cx="320"
        cy="290"
        r="6"
        fill="#4f46e5"
        className="anim-float"
        style={{ animationDelay: "1s" }}
      />
    </svg>

    <div className="engine-text">
      <h2>Set a New Password</h2>
      <p>Choose a strong password to keep your account secure.</p>
    </div>
  </div>
);

/* --- Main Component --- */

/* Stable machine-readable code from the API error envelope (message wording
   may change; the code won't). */
const getErrorCode = (error) => {
  try {
    return JSON.parse(error.message)?.code || null;
  } catch {
    return null;
  }
};

const RESET_TOKEN_ERROR_CODES = [
  "RESET_TOKEN_EXPIRED",
  "RESET_TOKEN_INVALID",
  "RESET_TOKEN_USED",
];

const formatCountdown = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = String(totalSeconds % 60).padStart(2, "0");
  return `${minutes}:${seconds}`;
};

const ChangePassword = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const goBack = useGoBack();
  // resetToken comes from the verify-otp step (single-use, ~10 min TTL).
  const { resetToken, expiresIn } = location.state || {};

  const passwordRef = useRef(null);

  const [form, setForm] = useState({
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [notification, setNotification] = useState("");
  const [notificationType, setNotificationType] = useState("success");

  const passwordRegex = /^(?=.*[0-9])(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/;

  const showNotification = (message, type = "success") => {
    setNotification(message);
    setNotificationType(type);
    setTimeout(() => setNotification(""), 3000);
  };

  useEffect(() => {
    passwordRef.current?.focus();
  }, []);

  /* Without a reset token (deep link / lost state) the flow must restart. */
  useEffect(() => {
    if (!resetToken) {
      navigate("/forgot-password", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetToken]);

  /* Token TTL countdown — when it lapses, restart from step 1. */
  const [secondsLeft, setSecondsLeft] = useState(
    Number.isFinite(expiresIn) ? expiresIn : null,
  );

  useEffect(() => {
    if (secondsLeft === null) return undefined;

    if (secondsLeft <= 0) {
      showNotification(
        "This reset link has expired. Please request a new OTP.",
        "error",
      );
      const t = setTimeout(
        () => navigate("/forgot-password", { replace: true }),
        1800,
      );
      return () => clearTimeout(t);
    }

    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondsLeft]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validate = () => {
    const newErrors = {};

    if (!form.password) {
      newErrors.password = "Password is required";
    } else if (!passwordRegex.test(form.password)) {
      newErrors.password =
        "Password must be at least 8 characters long and include 1 number and 1 special character";
    }

    if (!form.confirmPassword) {
      newErrors.confirmPassword = "Confirm your password";
    } else if (form.password !== form.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!validate()) return;

    setIsSubmitting(true);

    try {
      // The account is identified by the reset token — no email is sent.
      const response = await apiPost("user", "/reset-password", {
        resetToken,
        newPassword: form.password,
        confirmPassword: form.confirmPassword,
      });

      if (response.status === "success") {
        showNotification(
          response.message || "Password changed successfully!",
          "success",
        );
        setTimeout(() => {
          navigate("/login", { replace: true });
        }, 1000);
      } else {
        showNotification(
          response.message || "Failed to change password",
          "error",
        );
      }
    } catch (err) {
      console.error("Change Password Error:", err);
      showNotification(getErrorMessage(err), "error");

      // Expired / invalid / already-used token → restart from step 1.
      if (RESET_TOKEN_ERROR_CODES.includes(getErrorCode(err))) {
        setTimeout(() => {
          navigate("/forgot-password", { replace: true });
        }, 1800);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-auth-container">
      {/* Left Branding Panel */}
      <section className="left-branding">
        <NewPasswordVisual />
      </section>

      {/* Right Auth Interface */}
      <main className="right-interface">
        <AnimatePresence>
          {notification && (
            <motion.div
              className={`notification-bar notify-${notificationType}`}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              <Icons.Shield /> {notification}
            </motion.div>
          )}
        </AnimatePresence>

        <AuthCard>
          <button className="back-btn" onClick={goBack}>
            <Icons.ChevronLeft /> Back
          </button>

          <header className="header">
            <h1>Change Password</h1>
            <p>
              Choose a strong new password to secure your account.
              {secondsLeft !== null && secondsLeft > 0 && (
                <>
                  {" "}
                  <span style={{ fontWeight: 600 }}>
                    Link expires in {formatCountdown(secondsLeft)}
                  </span>
                </>
              )}
            </p>
          </header>

          <form onSubmit={handleSubmit} className="form-fields" noValidate>
            {/* New Password */}
            <div className="input-field">
              <div className="label-box">
                <label htmlFor="change-password">New Password</label>
              </div>
              <div className="control-box">
                <input
                  id="change-password"
                  ref={passwordRef}
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  placeholder="Enter a strong password"
                  onChange={(e) => handleChange("password", e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="toggle-btn"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <Icons.EyeOff /> : <Icons.Eye />}
                </button>
              </div>
              {errors.password && (
                <div className="error-text">{errors.password}</div>
              )}
            </div>

            {/* Confirm New Password */}
            <div className="input-field">
              <div className="label-box">
                <label htmlFor="change-confirm-password">
                  Confirm New Password
                </label>
              </div>
              <div className="control-box">
                <input
                  id="change-confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={form.confirmPassword}
                  placeholder="Re-enter the password"
                  onChange={(e) =>
                    handleChange("confirmPassword", e.target.value)
                  }
                  required
                />
                <button
                  type="button"
                  className="toggle-btn"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  aria-label={
                    showConfirmPassword ? "Hide password" : "Show password"
                  }
                >
                  {showConfirmPassword ? <Icons.EyeOff /> : <Icons.Eye />}
                </button>
              </div>
              {errors.confirmPassword && (
                <div className="error-text">{errors.confirmPassword}</div>
              )}
            </div>

            <div className="action-submit">
              <button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Updating..." : "Continue"}
              </button>
            </div>
          </form>

          <p className="onboarding-link">
            Remember your password? <Link to="/login">Back to Sign In</Link>
          </p>
        </AuthCard>
      </main>
    </div>
  );
};

export default ChangePassword;
